import React, { PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import Code from '@material-ui/icons/Code';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import BookmarksIcon from '@material-ui/icons/Bookmarks';
import InfoIcon from '@material-ui/icons/Info';
import BuildIcon from '@material-ui/icons/Build';
import AllInclusiveIcon from '@material-ui/icons/AllInclusive';
// import ApartmentIcon from '@material-ui/icons/Apartment';
// import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
// import SecurityIcon from '@material-ui/icons/Security';
import SettingsIcon from '@material-ui/icons/Settings';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import Footer from './Footer';
import {
  GuestHiddenElementsStyles,
  GuestRouteGuard,
  useIsGuest,
} from '../guest';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import SearchIcon from '@material-ui/icons/Search';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import MemoryIcon from '@material-ui/icons/Memory';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
// import AccessibilityNewIcon from '@material-ui/icons/AccessibilityNew';
import LinkIcon from '@material-ui/icons/Link';
// import FlashOnIcon from '@material-ui/icons/FlashOn';
// import AssignmentIcon from '@material-ui/icons/Assignment';
// import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { useLinkGroups } from '../links/useLinkGroups';
import { WIKI_SECTIONS } from '../wiki/wikiSections';
import { useIsLegacyWikiDocs } from '../wiki/useIsLegacyWikiDocs';
import { useLegacyWikiEnabled } from '../wiki/useLegacyWikiEnabled';
import { useWikiImportEnabled } from '../wikiImport/useWikiImportEnabled';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const isGuest = useIsGuest();
  const { pathname } = useLocation();
  const legacyWikiEnabled = useLegacyWikiEnabled();
  const wikiImportEnabled = useWikiImportEnabled();
  const inWikiDocs = useIsLegacyWikiDocs() && legacyWikiEnabled;
  const inGettingStarted = pathname.startsWith(
    '/docs/default/component/devportal-backstage',
  );
  const linkGroups = useLinkGroups();

  return (
    <SidebarPage>
      <GuestHiddenElementsStyles />
      <Sidebar>
        <SidebarLogo />
        {!isGuest && (
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
        )}
        <SidebarDivider />
        <SidebarGroup label="Main" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="home" text="Home" />

          {!isGuest && (
            <SidebarItem
              icon={BookmarksIcon}
              to="/docs/default/component/devportal-backstage"
              text="Getting Started"
            />
          )}

          {!isGuest && (
            <>
              <SidebarDivider />
              <SidebarItem icon={ExtensionIcon} to="/catalog" text="Catalog" />
            </>
          )}

          <SidebarItem icon={Code} to="api-docs" text="APIs">
            <SidebarSubmenu title="APIs">
              <SidebarSubmenuItem
                title="All"
                to="api-docs"
                icon={AllInclusiveIcon}
              />
              <SidebarSubmenuItem
                title="Informational"
                icon={InfoIcon}
                dropdownItems={[
                  {
                    title: 'Catalog',
                    to: '/api-docs?filters%5Bkind%5D=api&filters%5Bsystem%5D=system%3Adefault%2Fconnect-apis-v1',
                  },
                  {
                    title: 'Documentation',
                    to: '/docs/default/system/connect-apis-v1',
                  },
                ]}
              />
              {!isGuest && (
                <SidebarSubmenuItem
                  title="Operational"
                  icon={BuildIcon}
                  dropdownItems={[
                    {
                      title: 'Catalog',
                      to: '/api-docs?filters%5Bkind%5D=api&filters%5Bsystem%5D=system%3Adefault%2Fapi-solutions-apis-v0',
                    },
                    {
                      title: 'Documentation',
                      to: '/docs/default/system/api-solutions-apis-v0',
                    },
                  ]}
                />
              )}
            </SidebarSubmenu>
          </SidebarItem>

          {!isGuest && (
            <SidebarItem
              icon={LibraryBooks}
              to="docs"
              text="Docs"
              {...(inWikiDocs || inGettingStarted
                ? { activeClassName: '' }
                : {})}
            >
              <SidebarSubmenu title="Docs">
                <SidebarSubmenuItem
                  title="All"
                  to="docs"
                  icon={AllInclusiveIcon}
                />
                <SidebarSubmenuItem
                  title="IT"
                  icon={MemoryIcon}
                  dropdownItems={[
                    {
                      title: 'Architecture',
                      to: '/docs/default/system/architecture-docs',
                    },
                    {
                      title: 'Cybersecurity',
                      to: '/docs/default/system/cybersecurity',
                    },
                    {
                      title: 'DevOps Enablement',
                      to: '/docs/default/system/devops-enablement-docs',
                    },
                    {
                      title: 'Observability',
                      to: '/docs/default/system/observability-docs',
                    },
                  ]}
                />
                <SidebarSubmenuItem
                  title="Business"
                  icon={BusinessCenterIcon}
                  dropdownItems={[
                    {
                      title: 'API Solutions',
                      to: '/docs/default/system/api-solutions-docs',
                    },
                    {
                      title: 'Core Systems',
                      to: '/docs/default/system/core-systems-docs',
                    },
                    {
                      title: 'Digital Channels',
                      to: '/docs/default/system/digital-channels-docs',
                    },
                  ]}
                />
              </SidebarSubmenu>
            </SidebarItem>
          )}

          {!isGuest && legacyWikiEnabled && (
            <SidebarItem
              icon={MenuBookIcon}
              to={inWikiDocs ? pathname : 'wiki'}
              text="Legacy Wiki"
            >
              <SidebarSubmenu title="Legacy Wiki">
                <SidebarSubmenuItem
                  title="Wiki home"
                  to="wiki"
                  icon={MenuBookIcon}
                />
                {wikiImportEnabled && (
                  <SidebarSubmenuItem
                    title="Import"
                    to="wiki/import"
                    icon={CloudUploadIcon}
                  />
                )}
                <SidebarDivider />
                {WIKI_SECTIONS.map(tab => (
                  <SidebarSubmenuItem
                    key={tab.name}
                    title={tab.name}
                    to={tab.docsPath}
                    icon={LibraryBooks}
                  />
                ))}
              </SidebarSubmenu>
            </SidebarItem>
          )}

          {!isGuest && (
            <SidebarItem icon={GroupIcon} to="/groups" text="Groups">
              <SidebarSubmenu title="Groups">
                <SidebarSubmenuItem
                  title="All"
                  to="/groups"
                  icon={AllInclusiveIcon}
                />
                <SidebarSubmenuItem
                  title="IT"
                  icon={MemoryIcon}
                  dropdownItems={[
                    {
                      title: 'Architecture',
                      to: '/catalog/default/group/architecture',
                    },
                    {
                      title: 'Cybersecurity',
                      to: '/catalog/default/group/cybersecurity',
                    },
                    {
                      title: 'DevOps Enablement',
                      to: '/catalog/default/group/devops-enablement',
                    },
                    {
                      title: 'Observability',
                      to: '/catalog/default/group/observability',
                    },
                  ]}
                />
                <SidebarSubmenuItem
                  title="Business"
                  icon={BusinessCenterIcon}
                  dropdownItems={[
                    {
                      title: 'API Solutions',
                      to: '/catalog/default/group/api-solutions',
                    },
                    {
                      title: 'Core Systems',
                      to: '/catalog/default/group/core-systems',
                    },
                    {
                      title: 'Digital Channels',
                      to: '/catalog/default/group/digital-channels',
                    },
                  ]}
                />
              </SidebarSubmenu>
            </SidebarItem>
          )}

          {!isGuest && (
            <MyGroupsSidebarItem
              singularTitle="My Group"
              pluralTitle="My Groups"
              icon={GroupIcon}
            />
          )}

          {!isGuest && (
            <>
              <SidebarDivider />
              <SidebarItem icon={BuildIcon} text="Tools">
                <SidebarSubmenu title="Tools">
                  <SidebarSubmenuItem
                    title="FundData"
                    to="/docs/default/system/fund-data"
                    icon={AccountBalanceIcon}
                    dropdownItems={[
                      {
                        title: 'Legal Files',
                        to: '/gcs-explorer',
                      },
                    ]}
                  />
                </SidebarSubmenu>
              </SidebarItem>
            </>
          )}

          {!isGuest && linkGroups.length > 0 && (
            <SidebarItem icon={LinkIcon} to="links" text="Links">
              <SidebarSubmenu title="Links">
                {linkGroups.map(group => (
                  <SidebarSubmenuItem
                    key={group.title}
                    title={group.title}
                    icon={LinkIcon}
                    dropdownItems={group.links.map(link => ({
                      title: link.name,
                      to: link.url,
                    }))}
                  />
                ))}
              </SidebarSubmenu>
            </SidebarItem>
          )}
        </SidebarGroup>

        <SidebarDivider />

        {/*
        <RequirePermission
          permission={catalogEntityCreatePermission}
          errorPage={<></>}
        >

         <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        </RequirePermission>
        */}

        <SidebarSpace />

        <SidebarDivider />

        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarItem icon={SettingsIcon} to="/settings" text="Settings" />
        </SidebarGroup>
      </Sidebar>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          width: '100%',
        }}
      >
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 24 }}>
          <GuestRouteGuard>{children}</GuestRouteGuard>
        </div>
        <Footer />
      </div>
    </SidebarPage>
  );
};
